import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_REWARDS_ESSENCE_QUALITY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { qualityNames } from '@sorare/core/src/lib/players';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import type {
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables,
} from './__generated__/index.graphql';

const REWARDS_ESSENCE_PAGE_QUERY = gql`
  query RewardsEssenceLayoutQuery($sport: Sport!) {
    currentUser {
      slug
      cardShardsChests(sport: $sport) {
        id
        rarity
        thresholds {
          draws {
            probabilities {
              quality
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  RewardsEssenceLayoutQuery,
  RewardsEssenceLayoutQueryVariables
>;

const Root = styled.div`
  padding: var(--double-unit) 0;
`;
const Content = styled.div`
  display: grid;
  gap: var(--double-unit);

  @media ${laptopAndAbove} {
    grid-template-columns: 220px 1fr;
  }
`;

type Props = {
  sport: Sport;
  quality?: string;
  rarity?: string;
};

export const RewardsEssenceLayout = ({ sport, quality, rarity }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { data, loading } = useQuery(REWARDS_ESSENCE_PAGE_QUERY, {
    variables: {
      sport,
    },
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  const { currentUser } = data || {};
  const { cardShardsChests } = currentUser || {};

  if (!cardShardsChests) {
    return null;
  }

  const qualitiesPerRarity = Object.fromEntries(
    cardShardsChests.map(chest => [
      chest.rarity,
      [
        ...new Set(
          chest.thresholds.flatMap(t =>
            t.draws.flatMap(d => d.probabilities.map(p => p.quality))
          )
        ),
      ],
    ])
  );

  const qualities = [
    ...new Set(Object.values(qualitiesPerRarity).flat()),
  ].sort();

  const raritiesPerQuality = Object.fromEntries(
    qualities.map(q => [
      q,
      Object.entries(qualitiesPerRarity)
        .filter(([, qs]) => qs.includes(q))
        .map(([key]) => key),
    ])
  );

  return (
    <Root>
      <PageHeader
        text={{
          id: 'Rewards.Essence.essencePool',
          defaultMessage: 'Essence pool',
        }}
      />
      <FullWidthContainer>
        <Content>
          <div>
            <Vertical>
              {qualities.map(q => (
                <Link
                  key={q}
                  to={generateSportContextPath(
                    ANY_SPORT_REWARDS_ESSENCE_QUALITY_RARITY,
                    {
                      params: {
                        quality: q,
                        rarity: raritiesPerQuality[q][0],
                      },
                    }
                  )}
                >
                  <BodyM
                    as="p"
                    bold={q === quality}
                    color={
                      q === quality ? 'var(--c-blue-400)' : 'var(--c-nd-600)'
                    }
                  >
                    {qualityNames[q]}
                  </BodyM>
                </Link>
              ))}
            </Vertical>
          </div>
          <div>
            <Vertical gap={2}>
              <TabBar value={rarity} variant="flat" fullWidth>
                {!!quality &&
                  raritiesPerQuality[quality].map(r => (
                    <Tab
                      key={r}
                      value={r}
                      to={generateSportContextPath(
                        ANY_SPORT_REWARDS_ESSENCE_QUALITY_RARITY,
                        {
                          params: {
                            quality,
                            rarity: r,
                          },
                        }
                      )}
                      replace
                    >
                      <ScarcityIcon rarity={r as Rarity} />
                      {scarcityNames[r]}
                    </Tab>
                  ))}
              </TabBar>
              <Outlet />
            </Vertical>
          </div>
        </Content>
      </FullWidthContainer>
    </Root>
  );
};
